// src/components/CryptoReportView.js
import React, { useRef, useState, useEffect } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import RiskBreakdown from "./RiskBreakdown";
import PsychologicalProfile from "./PsychologicalProfile";
import RecommendationCard from "./RecommendationCard";
import PortfolioAllocationChart from "./PortfolioAllocationChart";
import StrategyRecommendations from "./StrategyRecommendations";
import { ARC_PRODUCTS, withTracking } from "../../config/affiliateLinks";

/**
 * Render a full crypto‑investor risk‑profile report and provide a one‑click PDF download.
 *
 * Dependencies (add once in your project root):
 *   npm install jspdf html2canvas
 */
const CryptoReportView = ({ results }) => {
  const reportRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  /**
   * Capture the report DOM node, split it across A4 pages if needed, and download as PDF.
   */
  const downloadReport = async () => {
    if (!reportRef.current || isGenerating) return;

    try {
      setIsGenerating(true);

      // 1️⃣ Capture report as a high‑resolution canvas
      const canvas = await html2canvas(reportRef.current, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: reportRef.current.scrollWidth,
        height: reportRef.current.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/png", 0.95);

      // 2️⃣ Prepare A4 portrait PDF with margins
      const pdf = new jsPDF({ 
        orientation: "p", 
        unit: "mm", 
        format: "a4",
        compress: true 
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15; // 15mm margins
      const contentWidth = pageWidth - (margin * 2);
      const contentHeight = pageHeight - (margin * 2);

      // 3️⃣ Calculate scaled dimensions
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;

      // 4️⃣ Add pages with proper positioning
      let heightLeft = imgHeight;
      let position = margin;

      // Add first page
      pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
      heightLeft -= contentHeight;

      // Add additional pages for overflow
      while (heightLeft > 0) {
        position = -(imgHeight - heightLeft) + margin;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
        heightLeft -= contentHeight;
      }

      // Save with timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      pdf.save(`crypto_risk_profile_${timestamp}.pdf`);

    } catch (error) {
      console.error('PDF generation failed:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Helpers: label & colour based on score
  const getRiskProfileLabel = (score) => {
    if (score <= 28) return "Ultra‑Conservative";
    if (score <= 39) return "Moderate / Balanced";
    if (score <= 49) return "Growth‑Oriented";
    if (score <= 58) return "Aggressive";
    return "Ultra‑Aggressive / DeGen";
  };

  const getRiskProfileBadgeClass = (score) => {
    if (score <= 28) return "bg-green-100 text-green-800 border border-green-200";
    if (score <= 39) return "bg-blue-100 text-blue-800 border border-blue-200";
    if (score <= 49) return "bg-yellow-100 text-yellow-800 border border-yellow-200";
    if (score <= 58) return "bg-orange-100 text-orange-800 border border-orange-200";
    return "bg-red-100 text-red-800 border border-red-200";
  };

  const getRiskColor = (score) => {
    if (score <= 28) return "#10b981"; // green
    if (score <= 39) return "#3b82f6"; // blue
    if (score <= 49) return "#f59e0b"; // yellow
    if (score <= 58) return "#f97316"; // orange
    return "#ef4444"; // red
  };

  return (
    <div className="min-h-screen bg-white/[0.03] py-4 sm:py-8">
      <div className="max-w-4xl mx-auto px-2 sm:px-4 lg:px-8">
        {/* ===== Report Body (captured for PDF) ===== */}
        <div 
          ref={reportRef} 
          className="bg-arc-elevated rounded-lg shadow-lg overflow-hidden mx-auto space-y-6 sm:space-y-8"
          style={{ 
            minHeight: isMobile ? 'auto' : '297mm',
            maxWidth: isMobile ? '100%' : '210mm',
            margin: '0 auto',
            padding: isMobile ? '16px' : '20mm'
          }}
        >
          {/* Header */}
          <div className="text-center mb-6 sm:mb-10 border-b border-arc-line pb-4 sm:pb-8">
            <div className="p-3 sm:p-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg mb-4 mx-auto max-w-sm sm:max-w-none">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2 leading-tight">
                Crypto Investor Risk Profile
              </h1>
              <p className="text-blue-100 text-sm sm:text-base">Detailed Analysis & Portfolio Recommendations</p>
            </div>
            <p className="text-arc-muted mt-4 text-sm sm:text-base">
              Generated on {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          {/* Overall Score Section */}
          <div className="mb-8 sm:mb-12">
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-xl sm:text-2xl font-semibold text-arc-ink mb-4 sm:mb-6">Your Risk Profile</h2>
              
              {/* Main Score Display */}
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 bg-white/[0.03] rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
                <div className="text-center">
                  <div 
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-xl sm:text-2xl font-bold text-white mb-2 mx-auto"
                    style={{ backgroundColor: getRiskColor(results.totalScore) }}
                  >
                    {results.totalScore}
                  </div>
                  <span className="text-xs sm:text-sm text-arc-muted">Risk Score</span>
                </div>
                <div className="text-center sm:text-left">
                  <div className={`inline-block px-3 sm:px-4 py-2 rounded-lg font-semibold text-sm sm:text-base ${getRiskProfileBadgeClass(results.totalScore)}`}>
                    {results.riskProfile.type}
                  </div>
                  <p className="text-arc-muted mt-2 text-sm">
                    {getRiskProfileLabel(results.totalScore)}
                  </p>
                </div>
              </div>

              <p className="text-arc-muted max-w-2xl mx-auto leading-relaxed text-sm sm:text-base px-2 sm:px-0">
                {results.riskProfile.description}
              </p>
            </div>

            {/* Risk Scale Visualization */}
            <div className="max-w-3xl mx-auto px-2 sm:px-0">
              <div className="relative">
                {/* Scale bars */}
                <div className="flex h-6 sm:h-8 rounded-lg overflow-hidden shadow-inner bg-white/[0.08]">
                  <div className="flex-1 bg-green-400 flex items-center justify-center text-xs font-medium text-white px-1">
                    <span className="hidden sm:inline">Conservative</span>
                    <span className="sm:hidden">Cons</span>
                  </div>
                  <div className="flex-1 bg-blue-400 flex items-center justify-center text-xs font-medium text-white px-1">
                    <span className="hidden sm:inline">Moderate</span>
                    <span className="sm:hidden">Mod</span>
                  </div>
                  <div className="flex-1 bg-yellow-400 flex items-center justify-center text-xs font-medium text-white px-1">
                    Growth
                  </div>
                  <div className="flex-1 bg-orange-400 flex items-center justify-center text-xs font-medium text-white px-1">
                    <span className="hidden sm:inline">Aggressive</span>
                    <span className="sm:hidden">Aggr</span>
                  </div>
                  <div className="flex-1 bg-red-400 flex items-center justify-center text-xs font-medium text-white px-1">
                    <span className="hidden sm:inline">Ultra-Aggressive</span>
                    <span className="sm:hidden">Ultra</span>
                  </div>
                </div>
                
                {/* Score ranges */}
                <div className="flex justify-between text-xs text-arc-dim mt-2 px-1">
                  <span>17-28</span>
                  <span>29-39</span>
                  <span>40-49</span>
                  <span>50-58</span>
                  <span>59-68</span>
                </div>

                {/* Score marker */}
                <div className="relative mt-3">
                  <div
                    className="absolute transform -translate-x-1/2"
                    style={{
                      left: `${((results.totalScore - 17) / (68 - 17)) * 100}%`,
                    }}
                  >
                    <div className="flex flex-col items-center">
                      <div 
                        className="w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-white shadow-lg"
                        style={{ backgroundColor: getRiskColor(results.totalScore) }}
                      />
                      <div className="w-0.5 h-3 sm:h-4 bg-arc-dim mt-1" />
                      <span className="text-xs font-semibold text-arc-muted mt-1">
                        You
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Portfolio Allocation Chart */}
          <div className="mb-8 sm:mb-12">
            <PortfolioAllocationChart allocation={results.portfolioAllocation} />
          </div>

          {/* Strategy Recommendations */}
          <div className="mb-8 sm:mb-12">
            <StrategyRecommendations
              riskScore={results.totalScore}
              vaultAllocation={results.vaultAllocation}
              profileType={results.riskProfile?.type}
              safetyOverrides={results.safetyOverrides}
              className=""
            />
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-8 sm:mb-12">
            {/* Risk Breakdown */}
            <div className="order-2 lg:order-1">
              <RiskBreakdown scores={results.scores} />
            </div>

            {/* Recommendations */}
            <div className="space-y-4 sm:space-y-6 order-1 lg:order-2">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-arc-ink mb-3 sm:mb-4 flex items-center">
                  <span className="w-2 h-5 sm:h-6 bg-arc-gold rounded mr-2 sm:mr-3"></span>
                  Investment Recommendations
                </h3>
                <RecommendationCard
                  title="Trading Strategy"
                  recommendations={results.riskProfile.recommendations}
                />
              </div>
              
              <RecommendationCard
                title="Psychological Insights"
                recommendations={results.riskProfile.psychologicalInsights}
              />
            </div>
          </div>

          {/* Psychological Profile */}
          <div className="mb-8 sm:mb-12">
            <PsychologicalProfile
              psychProfiles={results.psychProfiles}
              psychStrengths={results.psychStrengths || []}
              psychWeaknesses={results.psychWeaknesses || []}
            />
          </div>

          {/* ApexOne CTA — Risk Assessment results page is the highest-intent
              moment in the entire app. The user has just answered 15 deep
              questions about their goals and risk profile. This is the moment
              to offer a personalized strategy call. */}
          <div className="border-t border-arc-line pt-6 mt-8 sm:mt-12 mb-6">
            <div className="rounded-2xl border-2 border-arc-gold bg-gradient-to-br from-arc-gold/15 via-arc-gold/5 to-transparent p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                  <p className="eyebrow text-arc-gold mb-2">Recommended next step</p>
                  <h3 className="font-bold text-xl sm:text-2xl text-arc-ink mb-2">
                    Want a 1-on-1 strategy plan based on these results?
                  </h3>
                  <p className="text-sm text-arc-muted leading-relaxed mb-4 max-w-xl">
                    Your risk profile is unique. An ARC strategist can walk you
                    through how to actually deploy this allocation — which vaults,
                    in what order, and how to size your positions for your goals.
                  </p>
                  <a
                    href={withTracking(ARC_PRODUCTS.apexone.url, "risk-assessment-results")}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="btn-tactile btn-gold inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold"
                  >
                    Book your ApexOne strategy call →
                  </a>
                </div>
                <div className="hidden sm:flex flex-shrink-0 items-center justify-center w-24 h-24 rounded-2xl bg-arc-gold/20 border border-arc-gold/40">
                  <span className="text-4xl">🎯</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer/Disclaimer */}
          <div className="border-t border-arc-line pt-4 sm:pt-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
              <h4 className="font-semibold text-yellow-800 mb-2 text-sm sm:text-base flex items-center">
                <span className="mr-2">⚠️</span>
                <span className="hidden sm:inline">Important Disclaimer</span>
                <span className="sm:hidden">Disclaimer</span>
              </h4>
              <p className="text-xs sm:text-sm text-yellow-700 leading-relaxed">
                This risk assessment and portfolio allocation are for educational purposes only and do not constitute financial advice.
                Cryptocurrency investing involves significant risk of loss and is not suitable for all investors. Always conduct your own
                research and consider consulting with a qualified financial advisor before making investment decisions.
              </p>
            </div>
            
            <div className="text-center mt-4 sm:mt-6 text-xs text-arc-dim">
              <div className="block sm:inline">Report generated on {new Date().toLocaleDateString()}</div>
              <div className="block sm:inline sm:before:content-['•'] sm:before:mx-2">
                Crypto Risk Profile Assessment Tool
              </div>
            </div>
          </div>
        </div>

        {/* ===== Action buttons (excluded from PDF) ===== */}
        <div className="text-center mt-6 sm:mt-8 print:hidden px-4">
          <button
            onClick={downloadReport}
            disabled={isGenerating}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isGenerating ? (
              <>
                <svg className="animate-spin w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="hidden sm:inline">Generating PDF...</span>
                <span className="sm:hidden">Generating...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">Download Report (PDF)</span>
                <span className="sm:hidden">Download PDF</span>
              </>
            )}
          </button>
          
          <p className="text-xs sm:text-sm text-arc-dim mt-3 px-2">
            <span className="hidden sm:inline">Click to generate and download your personalized crypto risk profile report</span>
            <span className="sm:hidden">Generate your personalized report</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CryptoReportView;